/**
 * @file Positions.jsx
 * @description Manages display and handling of trading positions
 *
 * @section Responsibilities
 * - Display trading positions
 * - Handle position management
 * - Process position data
 * - Manage position state
 * - Handle position errors
 * - Provide position controls
 *
 * @section Dependencies
 * - PositionService: Position management
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Position API: Position operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: Position controls
 *
 * @section State Management
 * - positionState: Position status
 * - dataState: Data status
 * - controlState: Control status
 * - errorState: Error handling
 *
 * @section Events
 * - onPositionUpdate: Position updates
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Array} positions - Trading positions
 * @prop {Function} onUpdate - Update callback
 * @prop {Object} initialData - Initial position data
 * @prop {Boolean} autoUpdate - Controls auto-updates
 */

import React, { useState, useEffect } from "react";
import Widget from "../Widget";
import { getFromSession } from "../../../../utils/sessionUtils";
import { formatAmount } from "../../../../utils/formatAmount";
import { format } from "rsuite/esm/internals/utils/date";

function Positions({ getClosingPosition, positions = [] }) {
  const [selectedPosition, setSelectedPosition] = useState("");
  const sessionRole = getFromSession("role");
  const isCustomer = sessionRole === "customer";
  const handleSelectedPosition = (positionData, isChecked) => {
    if (isChecked) {
      setSelectedPosition(positionData);
    } else {
      setSelectedPosition("");
    }
  };

  const handleClosingPosition = () => {
    getClosingPosition(selectedPosition);
  };

  return (
    <Widget name={"positions-history"} width={"full-width"}>
      {getClosingPosition && (
        <div className="heading">
          <h2 className="left-title">Positions</h2>
          {!isCustomer && (
            <button
              onClick={handleClosingPosition}
              className="secondary small"
              disabled={selectedPosition == ""}
            >
              <span>Close</span>
            </button>
          )}
        </div>
      )}
      <table className="basic-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Change</th>
            <th>Value</th>
            <th>Cost basis</th>
            <th>Gain/Loss</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {positions?.map((position = {}) => (
            <tr key={position.id}>
              <td className="symbol">
                <p className="order-text">{position.symbol}</p>
              </td>
              <td className="qty">
                <p className="order-text">{position.quantity}</p>
              </td>
              <td className="qty">
                <p className="order-text">$ {formatAmount(position.lastPrice, 2)}</p>
              </td>
              <td className="qty">
                <p
                  className="order-text"
                  style={{
                    color: position.change >= 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  $ {formatAmount(position.change, 2)} <br />
                  <b>% {(position.changePerc ?? 0).toFixed(2)}</b>
                </p>
              </td>
              <td className="qty">
                <p className="order-text">
                  $ {formatAmount(position.value, 2)}
                </p>
              </td>
              <td className="qty">
                <p className="order-text">
                  $ {formatAmount(position.costBasis, 2)}
                  <br />
                  <b>
                    $ {formatAmount(position.costBasis / Math.abs(position.quantity), 2)}
                  </b>
                </p>
              </td>
              <td className="cost-basis">
                <p
                  className="order-text"
                  style={{
                    color: position.gainLoss >= 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  ${formatAmount(position.gainLoss, 2)} <br />
                  <b>% {formatAmount(position.gainLossPerc, 2)}</b>
                </p>
              </td>
              <td className="check first">
                {!isCustomer && (
                  <input
                    type="checkbox"
                    checked={selectedPosition?.id === position?.id}
                    name="pos_checkboxes"
                    onChange={(e) =>
                      handleSelectedPosition(
                        {
                          id: position?.id,
                          symbol: position?.symbol,
                          quantity: position?.quantity,
                        },
                        e.target.checked
                      )
                    }
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Widget>
  );
}

export default Positions;
